import React, { useMemo } from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { BarChart as ChartIcon } from '@mui/icons-material';
import { VegaEmbed } from 'react-vega';
import type { Spec as VegaSpec } from 'vega';

import { Widget } from '../../../types/canvas.types';
import { WidgetWrapper } from './WidgetWrapper';
import { useWidgetData } from '../../../data/canvas/useWidgetData';
import { useAccount } from '../../../auth';

interface ChartWidgetProps {
  widget: Widget;
  onEdit?: (widgetId: string) => void;
  onDelete?: (widgetId: string) => void;
  onDuplicate?: (widgetId: string) => void;
}

/**
 * Chart type presets using Vega-Lite specifications
 */
const CHART_PRESETS: Record<string, any> = {
  bar: {
    mark: 'bar',
    encoding: {
      x: { field: 'x', type: 'nominal', axis: { labelAngle: -45 } },
      y: { field: 'y', type: 'quantitative' },
      tooltip: [
        { field: 'x', type: 'nominal', title: 'Category' },
        { field: 'y', type: 'quantitative', title: 'Value' },
      ],
    },
  },
  line: {
    mark: { type: 'line', point: true },
    encoding: {
      x: { field: 'x', type: 'temporal', axis: { format: '%Y-%m-%d' } },
      y: { field: 'y', type: 'quantitative' },
      tooltip: [
        { field: 'x', type: 'temporal', title: 'Date' },
        { field: 'y', type: 'quantitative', title: 'Value' },
      ],
    },
  },
  area: {
    mark: { type: 'area', line: true, point: true },
    encoding: {
      x: { field: 'x', type: 'temporal', axis: { format: '%Y-%m-%d' } },
      y: { field: 'y', type: 'quantitative' },
      tooltip: [
        { field: 'x', type: 'temporal', title: 'Date' },
        { field: 'y', type: 'quantitative', title: 'Value' },
      ],
    },
  },
  pie: {
    mark: { type: 'arc', innerRadius: 0 },
    encoding: {
      theta: { field: 'y', type: 'quantitative' },
      color: { field: 'x', type: 'nominal', legend: { title: 'Category' } },
      tooltip: [
        { field: 'x', type: 'nominal', title: 'Category' },
        { field: 'y', type: 'quantitative', title: 'Value' },
      ],
    },
  },
  donut: {
    mark: { type: 'arc', innerRadius: 50 },
    encoding: {
      theta: { field: 'y', type: 'quantitative' },
      color: { field: 'x', type: 'nominal', legend: { title: 'Category' } },
      tooltip: [
        { field: 'x', type: 'nominal', title: 'Category' },
        { field: 'y', type: 'quantitative', title: 'Value' },
      ],
    },
  },
  scatter: {
    mark: { type: 'point', filled: true, size: 100 },
    encoding: {
      x: { field: 'x', type: 'quantitative' },
      y: { field: 'y', type: 'quantitative' },
      color: { field: 'category', type: 'nominal' },
      tooltip: [
        { field: 'x', type: 'quantitative', title: 'X' },
        { field: 'y', type: 'quantitative', title: 'Y' },
        { field: 'category', type: 'nominal', title: 'Category' },
      ],
    },
  },
  heatmap: {
    mark: 'rect',
    encoding: {
      x: { field: 'x', type: 'nominal' },
      y: { field: 'y', type: 'nominal' },
      color: {
        field: 'value',
        type: 'quantitative',
        scale: { scheme: 'blues' },
      },
      tooltip: [
        { field: 'x', type: 'nominal', title: 'X' },
        { field: 'y', type: 'nominal', title: 'Y' },
        { field: 'value', type: 'quantitative', title: 'Value' },
      ],
    },
  },
};

/**
 * Transform OData result to chart data format
 */
function transformDataForChart(
  data: any[],
  xField?: string,
  yField?: string,
  categoryField?: string,
): any[] {
  if (!data || data.length === 0) return [];

  // If fields are specified, use them
  if (xField && yField) {
    return data.map((item) => ({
      x: item[xField],
      y: item[yField],
      category: categoryField ? item[categoryField] : undefined,
      ...item, // Keep all original fields for tooltip
    }));
  }

  // Auto-detect: use first string/date field as x, first numeric field as y
  const sampleRow = data[0];
  const fields = Object.keys(sampleRow);

  const numericFields = fields.filter((f) => typeof sampleRow[f] === 'number');
  const stringFields = fields.filter(
    (f) => typeof sampleRow[f] === 'string' && !sampleRow[f].includes('T'), // Exclude ISO dates
  );
  const dateFields = fields.filter(
    (f) => typeof sampleRow[f] === 'string' && sampleRow[f].includes('T'), // ISO date strings
  );

  const autoXField =
    dateFields[0] || stringFields[0] || numericFields[0] || fields[0];
  const autoYField = numericFields.find((f) => f !== autoXField) || fields[1];

  return data.map((item) => ({
    x: item[autoXField],
    y: item[autoYField],
    ...item,
  }));
}

export const ChartWidget: React.FC<ChartWidgetProps> = ({
  widget,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  // Get account ID from auth context
  const { currentAccount } = useAccount();
  const accountId = currentAccount?.id || '';

  // Get widget data (only fetch when accountId is available)
  const {
    data: widgetDataResult,
    isLoading,
    error,
  } = useWidgetData(widget, accountId, {
    enabled: !!accountId && !!widget.data_source,
  });

  // Extract data and metadata from result
  const widgetData = widgetDataResult?.data || [];
  const skippedFilters = widgetDataResult?.skippedFilters || [];

  // Get chart configuration from widget
  const chartConfig = widget.configuration || {};
  const chartType = chartConfig['chart_type'] || 'bar';
  const xField = chartConfig['x_field'];
  const yField = chartConfig['y_field'];
  const categoryField = chartConfig['category_field'];
  const customSpec = chartConfig['vega_spec']; // Allow custom Vega-Lite specs

  // DEBUG: Log chart widget state
  console.log('📊 ChartWidget Debug:', {
    widgetId: widget.id,
    hasData: !!widgetData,
    dataLength: widgetData?.length,
    isLoading,
    error: error?.message,
    chartConfig,
    xField,
    yField,
    widgetDataSample: widgetData?.[0],
    skippedFilters,
  });

  // Transform data for chart
  const chartData = useMemo(() => {
    if (!widgetData || widgetData.length === 0) {
      console.log('📊 No data to transform');
      return [];
    }
    const transformed = transformDataForChart(
      widgetData,
      xField,
      yField,
      categoryField,
    );
    console.log('📊 Transformed chart data:', {
      originalLength: widgetData.length,
      transformedLength: transformed.length,
      firstItem: transformed[0],
    });
    return transformed;
  }, [widgetData, xField, yField, categoryField]);

  // Build Vega-Lite specification
  const vegaSpec: any = useMemo(() => {
    if (customSpec) {
      // Use custom spec if provided
      return {
        ...customSpec,
        data: { values: chartData },
        width: 'container',
        height: 'container',
      };
    }

    // Use preset
    const preset = CHART_PRESETS[chartType] || CHART_PRESETS['bar'];
    return {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      description:
        chartConfig.description || widget.configuration?.title || 'Chart',
      data: { values: chartData },
      width: 'container',
      height: 'container',
      autosize: { type: 'fit', contains: 'padding' },
      ...preset,
      config: {
        view: { stroke: 'transparent' },
        axis: { grid: true, gridOpacity: 0.3 },
      },
    };
  }, [chartData, chartType, customSpec, chartConfig, widget.configuration]);

  // Render loading state
  if (isLoading) {
    return (
      <WidgetWrapper
        widget={widget}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="text.secondary">
            Loading chart data...
          </Typography>
        </Box>
      </WidgetWrapper>
    );
  }

  // Render error state
  if (error) {
    return (
      <WidgetWrapper
        widget={widget}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
      >
        <Alert severity="error" sx={{ m: 2 }}>
          Failed to load chart data: {error.message}
        </Alert>
      </WidgetWrapper>
    );
  }

  // Render empty state
  if (!chartData || chartData.length === 0) {
    return (
      <WidgetWrapper
        widget={widget}
        onEdit={onEdit}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
          textAlign="center"
        >
          <ChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No chart data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure a data source to display charts
          </Typography>
        </Box>
      </WidgetWrapper>
    );
  }

  // Render chart
  return (
    <WidgetWrapper
      widget={widget}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        {/* Warning overlay for skipped filters */}
        {skippedFilters.length > 0 && (
          <Alert
            severity="warning"
            sx={{
              mb: 1,
              fontSize: '0.75rem',
              py: 0.5,
            }}
          >
            Some filters not applied: {skippedFilters.join(', ')}
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            height: skippedFilters.length > 0 ? 'calc(100% - 48px)' : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <VegaEmbed
            spec={vegaSpec}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Box>
    </WidgetWrapper>
  );
};
